import React from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useEaseOutProgress } from 'hooks/use-ease-out-progress';
import styles from './style.module.scss';

export interface SellListGaugeProps {
  /** Sale-to-list price ratio as a fraction (e.g. 0.973 for 97.3%). */
  sellListRatio: number;
  /** Average days on market, used in the summary sentence below the gauge. */
  daysOnMarket?: number;
  areaName?: string;
}

const GAUGE_SIZE = 172;
const STROKE_WIDTH = 15;
const RADIUS = 66;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Circular sale-to-list ratio gauge. Animates from 0 to the live ratio on
 * mount with an ease-out cubic curve, matching the collapsible section's
 * mount/remount behaviour (it fully unmounts when the accordion item closes).
 */
const SellListGauge = ({ sellListRatio, daysOnMarket, areaName }: SellListGaugeProps) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatNumber } = useI18nFormatting();
  const progress = useEaseOutProgress([sellListRatio]);

  if (!sellListRatio || sellListRatio <= 0) {
    return null;
  }

  const target = Math.min(1.5, sellListRatio);
  const percent = target * 100 * progress;
  const dashOffset = CIRCUMFERENCE * (1 - target * progress);
  const area = areaName?.trim();
  const pct = formatNumber(sellListRatio * 100, { maximumFractionDigits: 1 });
  const dom = daysOnMarket && daysOnMarket > 0 ? formatNumber(daysOnMarket) : undefined;

  const isSeller = sellListRatio >= 1.0;
  const interpolation = { area: area || 'This market', pct, dom: dom || '' };
  const summary = dom
    ? (isSeller
      ? t(LOCALE_COMMON_PATHS.MARKETFAQ_SELLLISTGAUGE_SUMMARYSELLER, { ...interpolation, defaultValue: '{{area}} homes sell for about {{pct}}% of asking price, on average in just {{dom}} days — a brisk, competitive market that still leaves a little room to negotiate.' })
      : t(LOCALE_COMMON_PATHS.MARKETFAQ_SELLLISTGAUGE_SUMMARYBUYER, { ...interpolation, defaultValue: '{{area}} homes sell for about {{pct}}% of asking price, on average in about {{dom}} days — buyers have some room to negotiate.' }))
    : (isSeller
      ? t(LOCALE_COMMON_PATHS.MARKETFAQ_SELLLISTGAUGE_SUMMARYSELLERNODOM, { ...interpolation, defaultValue: '{{area}} homes sell for about {{pct}}% of asking price, on average — a brisk, competitive market that still leaves a little room to negotiate.' })
      : t(LOCALE_COMMON_PATHS.MARKETFAQ_SELLLISTGAUGE_SUMMARYBUYERNODOM, { ...interpolation, defaultValue: '{{area}} homes sell for about {{pct}}% of asking price, on average — buyers have some room to negotiate.' }));

  return (
    <div className={styles.component}>
      <div className={styles.gauge} style={{ width: GAUGE_SIZE, height: GAUGE_SIZE }}>
        <svg width={GAUGE_SIZE} height={GAUGE_SIZE} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}>
          <circle
            cx={GAUGE_SIZE / 2}
            cy={GAUGE_SIZE / 2}
            r={RADIUS}
            fill="none"
            className={styles.track}
            strokeWidth={STROKE_WIDTH}
          />
          <circle
            cx={GAUGE_SIZE / 2}
            cy={GAUGE_SIZE / 2}
            r={RADIUS}
            fill="none"
            className={styles.arc}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${GAUGE_SIZE / 2} ${GAUGE_SIZE / 2})`}
          />
        </svg>
        <div className={styles.gaugeLabel}>
          <span className={styles.gaugeValue}>{percent.toFixed(1)}%</span>
          <span className={styles.gaugeSubLabel}>
            {t(LOCALE_COMMON_PATHS.MARKETFAQ_SELLLISTGAUGE_LABEL, { defaultValue: 'sale / list' })}
          </span>
        </div>
      </div>
      <p className={styles.summary}>{summary}</p>
    </div>
  );
};

export default SellListGauge;
